import { useState, useEffect, useMemo } from 'react';
import { format, addDays, startOfWeek, endOfWeek, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, isSameMonth, addWeeks, subWeeks, addMonths, subMonths, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ChevronLeft, ChevronRight, Plus, X, Clock, User, Ship,
  CloudRain, Pencil, Printer, CalendarDays, CalendarRange, Calendar as CalendarIcon, Ban, Trash2,
  ClipboardCheck, Send, FileText
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BOAT_COLORS = ['#0a57a4', '#099dbb', '#e67e22'];
const BOAT_LABELS = ['MARINU', 'DAMIANU', 'AMBROGGIU'];

const STATUS_COLORS = {
  pending: { bg: '#FEF3C7', text: '#92400E', border: '#F59E0B' },
  confirmed: { bg: '#DBEAFE', text: '#1E40AF', border: '#3B82F6' },
  paid: { bg: '#D1FAE5', text: '#065F46', border: '#10B981' },
  completed: { bg: '#E0E7FF', text: '#3730A3', border: '#6366F1' },
  cancelled: { bg: '#FEE2E2', text: '#991B1B', border: '#EF4444' }
};

const STATUS_LABELS = {
  pending: 'En attente',
  confirmed: 'Confirmée',
  paid: 'Payée',
  completed: 'Terminée',
  cancelled: 'Annulée'
};

export default function AgendaView({ token }) {
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 1024;
  const [view, setView] = useState(isMobile ? 'day' : 'week');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [bookings, setBookings] = useState([]);
  const [boats, setBoats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewBooking, setShowNewBooking] = useState(false);
  const [showEditBooking, setShowEditBooking] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [blockedDates, setBlockedDates] = useState([]);
  const [showBlockDates, setShowBlockDates] = useState(false);
  const [blockForm, setBlockForm] = useState({ start_date: '', end_date: '', reason: '' });

  const authHeaders = {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  };

  const dateRange = useMemo(() => {
    if (view === 'day') {
      return { start: currentDate, end: currentDate };
    } else if (view === 'week') {
      return { start: startOfWeek(currentDate, { weekStartsOn: 1 }), end: endOfWeek(currentDate, { weekStartsOn: 1 }) };
    } else {
      return { start: startOfMonth(currentDate), end: endOfMonth(currentDate) };
    }
  }, [view, currentDate]);

  const days = useMemo(() => {
    return eachDayOfInterval({ start: dateRange.start, end: dateRange.end });
  }, [dateRange]);

  useEffect(() => {
    fetchAgenda();
    fetchBlockedDates();
  }, [dateRange]);

  const fetchAgenda = async () => {
    setLoading(true);
    try {
      const start = format(dateRange.start, 'yyyy-MM-dd');
      const end = format(dateRange.end, 'yyyy-MM-dd');
      const res = await fetch(`${API}/admin/agenda?start_date=${start}&end_date=${end}`, { headers: authHeaders });
      if (!res.ok) throw new Error('Failed');
      const data = await res.json();
      setBookings(data.bookings);
      setBoats(data.boats);
    } catch (e) {
      toast.error('Erreur chargement agenda');
    } finally {
      setLoading(false);
    }
  };

  const navigate = (dir) => {
    if (view === 'day') setCurrentDate(prev => addDays(prev, dir));
    else if (view === 'week') setCurrentDate(prev => dir > 0 ? addWeeks(prev, 1) : subWeeks(prev, 1));
    else setCurrentDate(prev => dir > 0 ? addMonths(prev, 1) : subMonths(prev, 1));
  };

  const goToday = () => setCurrentDate(new Date());

  const getBookingsForDay = (day) => {
    const dateStr = format(day, 'yyyy-MM-dd');
    return bookings.filter(b => b.date === dateStr);
  };

  const isDateBlocked = (day) => {
    const dateStr = format(day, 'yyyy-MM-dd');
    return blockedDates.some(b => dateStr >= b.start_date && dateStr <= b.end_date);
  };

  const getBlockReason = (day) => {
    const dateStr = format(day, 'yyyy-MM-dd');
    const blocked = blockedDates.find(b => dateStr >= b.start_date && dateStr <= b.end_date);
    return blocked?.reason || 'Date bloquée';
  };

  const fetchBlockedDates = async () => {
    try {
      const res = await fetch(`${API}/admin/blocked-dates`, { headers: authHeaders });
      if (res.ok) setBlockedDates(await res.json());
    } catch (e) { console.error(e); }
  };

  const createBlockedDates = async () => {
    if (!blockForm.start_date || !blockForm.end_date) {
      toast.error('Sélectionnez les dates de début et fin');
      return;
    }
    if (blockForm.end_date < blockForm.start_date) {
      toast.error('La date de fin doit être après la date de début');
      return;
    }
    try {
      const res = await fetch(`${API}/admin/blocked-dates`, {
        method: 'POST', headers: authHeaders,
        body: JSON.stringify(blockForm)
      });
      if (!res.ok) throw new Error();
      toast.success('Dates bloquées avec succès');
      setShowBlockDates(false);
      setBlockForm({ start_date: '', end_date: '', reason: '' });
      fetchBlockedDates();
    } catch { toast.error('Erreur lors du blocage des dates'); }
  };

  const deleteBlockedDate = async (id) => {
    try {
      const res = await fetch(`${API}/admin/blocked-dates/${id}`, {
        method: 'DELETE', headers: authHeaders
      });
      if (!res.ok) throw new Error();
      toast.success('Date débloquée');
      fetchBlockedDates();
    } catch { toast.error('Erreur'); }
  };

  const getBookingsForDayAndBoat = (day, boatId) => {
    const dateStr = format(day, 'yyyy-MM-dd');
    return bookings.filter(b => b.date === dateStr && b.boat_id === boatId);
  };

  const getBoatIndex = (boatId) => boats.findIndex(b => b.id === boatId);

  const getTitle = () => {
    if (view === 'day') return format(currentDate, 'EEEE d MMMM yyyy', { locale: fr });
    if (view === 'week') return `${format(dateRange.start, 'd MMM', { locale: fr })} - ${format(dateRange.end, 'd MMM yyyy', { locale: fr })}`;
    return format(currentDate, 'MMMM yyyy', { locale: fr });
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="space-y-4" data-testid="agenda-view">
      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 bg-white p-3 lg:p-4 rounded-sm border border-[#dec5ae]/30">
        <div className="flex items-center gap-1">
          <Button variant="outline" size="sm" onClick={() => navigate(-1)} data-testid="agenda-prev">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={goToday} data-testid="agenda-today">Aujourd'hui</Button>
          <Button variant="outline" size="sm" onClick={() => navigate(1)} data-testid="agenda-next">
            <ChevronRight className="w-4 h-4" />
          </Button>
          <h2 className="font-heading text-sm lg:text-lg text-[#0a57a4] capitalize ml-1 lg:ml-2 truncate" data-testid="agenda-title">{getTitle()}</h2>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {/* View toggles */}
          <div className="flex bg-[#FDFBF7] rounded-sm border border-[#dec5ae]/30 overflow-hidden">
            {[
              { id: 'day', label: 'Jour', icon: CalendarDays },
              { id: 'week', label: 'Sem.', icon: CalendarRange },
              { id: 'month', label: 'Mois', icon: CalendarIcon }
            ].map(v => (
              <button
                key={v.id}
                onClick={() => setView(v.id)}
                className={`flex items-center gap-1 px-2 lg:px-3 py-1.5 text-xs transition-colors ${view === v.id ? 'bg-[#0a57a4] text-white' : 'text-[#858275] hover:text-[#0a57a4]'}`}
                data-testid={`view-${v.id}`}
              >
                <v.icon className="w-3.5 h-3.5" />
                {v.label}
              </button>
            ))}
          </div>
          <Button size="sm" variant="outline" onClick={handlePrint} className="hidden sm:flex" data-testid="agenda-print">
            <Printer className="w-4 h-4 mr-1" /> Imprimer
          </Button>
          <Button size="sm" className="bg-[#099dbb] hover:bg-[#0a57a4] text-white" onClick={() => { setSelectedSlot(null); setShowNewBooking(true); }} data-testid="agenda-new-booking">
            <Plus className="w-4 h-4 mr-1" /> <span className="hidden sm:inline">Réservation</span>
          </Button>
          <Button size="sm" variant="outline" className="border-red-400 text-red-600 hover:bg-red-50" onClick={() => setShowBlockDates(true)} data-testid="agenda-block-dates">
            <Ban className="w-4 h-4 mr-1" /> <span className="hidden sm:inline">Bloquer dates</span>
          </Button>
        </div>
      </div>

      {/* Boat legend */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4 px-1">
        <div className="flex items-center gap-3 flex-wrap">
          {boats.map((boat, i) => (
            <div key={boat.id} className="flex items-center gap-1.5 text-xs">
              <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: BOAT_COLORS[i] || '#666' }} />
              <span className="text-[#858275]">{BOAT_LABELS[i] || boat.name}</span>
            </div>
          ))}
        </div>
        <div className="flex items-center gap-2 flex-wrap text-xs text-[#858275]">
          {Object.entries(STATUS_LABELS).filter(([k]) => k !== 'cancelled').map(([key, label]) => (
            <div key={key} className="flex items-center gap-1">
              <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: STATUS_COLORS[key]?.border }} />
              {label}
            </div>
          ))}
        </div>
      </div>

      {/* Blocked dates list */}
      {blockedDates.length > 0 && (
        <div className="flex flex-wrap gap-2 px-1">
          {blockedDates.map(b => (
            <div key={b.id} className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-1.5 text-xs">
              <Ban className="w-3.5 h-3.5 text-red-500" />
              <span className="text-red-700 font-medium">
                {b.start_date === b.end_date 
                  ? new Date(b.start_date + 'T12:00:00').toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })
                  : `${new Date(b.start_date + 'T12:00:00').toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })} → ${new Date(b.end_date + 'T12:00:00').toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}`
                }
              </span>
              {b.reason && <span className="text-red-500">{b.reason}</span>}
              <button onClick={() => deleteBlockedDate(b.id)} className="text-red-400 hover:text-red-600 ml-1" title="Débloquer">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center h-96 bg-white rounded-sm border border-[#dec5ae]/30">
          <div className="w-8 h-8 border-2 border-[#099dbb]/30 border-t-[#099dbb] rounded-full animate-spin" />
        </div>
      ) : (
        <>
          {/* Day View */}
          {view === 'day' && <DayView day={currentDate} boats={boats} bookings={getBookingsForDay(currentDate)} onEdit={setShowEditBooking} onNewSlot={(slot) => { setSelectedSlot(slot); setShowNewBooking(true); }} />}

          {/* Week View */}
          {view === 'week' && (
            <div className="bg-white rounded-sm border border-[#dec5ae]/30 overflow-x-auto">
              <table className="w-full border-collapse min-w-[600px] lg:min-w-[900px]">
                <thead>
                  <tr className="border-b border-[#dec5ae]/30">
                    <th className="p-3 text-left text-xs text-[#858275] w-24 bg-[#FDFBF7]">Bateau</th>
                    {days.map(day => (
                      <th key={day.toISOString()} className={`p-3 text-center text-xs border-l border-[#dec5ae]/20 ${isDateBlocked(day) ? 'bg-red-50' : isSameDay(day, new Date()) ? 'bg-[#099dbb]/10' : 'bg-[#FDFBF7]'}`}>
                        <div className="text-[#858275] uppercase">{format(day, 'EEE', { locale: fr })}</div>
                        <div className={`text-lg font-heading ${isDateBlocked(day) ? 'text-red-500' : isSameDay(day, new Date()) ? 'text-[#099dbb]' : 'text-[#0a57a4]'}`}>{format(day, 'd')}</div>
                        {isDateBlocked(day) && <div className="text-[10px] text-red-500 font-medium mt-0.5"><Ban className="w-3 h-3 inline" /> Bloqué</div>}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {boats.map((boat, bi) => (
                    <tr key={boat.id} className="border-b border-[#dec5ae]/20 last:border-0">
                      <td className="p-2 text-xs font-medium text-[#0a57a4] bg-[#FDFBF7] border-r border-[#dec5ae]/20">
                        <div className="flex items-center gap-2">
                          <div className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: BOAT_COLORS[bi] }} />
                          {BOAT_LABELS[bi]}
                        </div>
                      </td>
                      {days.map(day => {
                        const dayBookings = getBookingsForDayAndBoat(day, boat.id);
                        const blocked = isDateBlocked(day);
                        return (
                          <td
                            key={day.toISOString()}
                            className={`p-1 align-top border-l border-[#dec5ae]/20 min-h-[80px] transition-colors ${blocked ? 'bg-red-50/60 cursor-not-allowed' : `cursor-pointer hover:bg-[#099dbb]/5 ${isSameDay(day, new Date()) ? 'bg-[#099dbb]/5' : ''}`}`}
                            onClick={() => {
                              if (blocked) return;
                              setSelectedSlot({ date: format(day, 'yyyy-MM-dd'), boat_id: boat.id });
                              setShowNewBooking(true);
                            }}
                          >
                            <div className="space-y-1 min-h-[70px]">
                              {blocked && dayBookings.length === 0 && (
                                <div className="text-[10px] text-red-400 text-center py-2 italic">{getBlockReason(day)}</div>
                              )}
                              {dayBookings.map(b => (
                                <BookingChip key={b.id} booking={b} boatIndex={bi} onClick={() => setShowEditBooking(b)} />
                              ))}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Month View */}
          {view === 'month' && (
            <div className="bg-white rounded-sm border border-[#dec5ae]/30">
              <div className="grid grid-cols-7 border-b border-[#dec5ae]/30">
                {['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'].map(d => (
                  <div key={d} className="p-2 text-center text-xs font-medium text-[#858275] bg-[#FDFBF7]">{d}</div>
                ))}
              </div>
              <div className="grid grid-cols-7">
                {(() => {
                  const monthStart = startOfMonth(currentDate);
                  const calStart = startOfWeek(monthStart, { weekStartsOn: 1 });
                  const monthEnd = endOfMonth(currentDate);
                  const calEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });
                  const calDays = eachDayOfInterval({ start: calStart, end: calEnd });
                  return calDays.map(day => {
                    const dayBookings = getBookingsForDay(day);
                    const isCurrentMonth = isSameMonth(day, currentDate);
                    const isToday = isSameDay(day, new Date());
                    const blocked = isDateBlocked(day);
                    return (
                      <div
                        key={day.toISOString()}
                        className={`min-h-[60px] lg:min-h-[100px] p-1 lg:p-1.5 border-b border-r border-[#dec5ae]/15 transition-colors ${blocked ? 'bg-red-50/60 cursor-not-allowed' : 'cursor-pointer hover:bg-[#099dbb]/5'} ${!isCurrentMonth ? 'opacity-40' : ''} ${isToday && !blocked ? 'bg-[#099dbb]/10' : ''}`}
                        onClick={() => { if (!blocked) { setCurrentDate(day); setView('day'); } }}
                      >
                        <div className={`text-xs font-medium mb-1 ${blocked ? 'text-red-500' : isToday ? 'text-[#099dbb] font-bold' : 'text-[#0a57a4]'}`}>
                          {format(day, 'd')} {blocked && <Ban className="w-3 h-3 inline ml-0.5" />}
                        </div>
                        <div className="space-y-0.5">
                          {blocked && dayBookings.length === 0 && (
                            <div className="text-[9px] text-red-400 italic">{getBlockReason(day)}</div>
                          )}
                          {dayBookings.slice(0, 3).map(b => (
                            <BookingChip key={b.id} booking={b} boatIndex={getBoatIndex(b.boat_id)} compact onClick={(e) => { e.stopPropagation(); setShowEditBooking(b); }} />
                          ))}
                          {dayBookings.length > 3 && (
                            <div className="text-[10px] text-[#858275] pl-1">+{dayBookings.length - 3} autres</div>
                          )}
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>
            </div>
          )}
        </>
      )}

      {/* New Booking Dialog */}
      <NewBookingDialog
        open={showNewBooking}
        onClose={() => { setShowNewBooking(false); setSelectedSlot(null); }}
        token={token}
        boats={boats}
        prefill={selectedSlot}
        onSuccess={() => { setShowNewBooking(false); setSelectedSlot(null); fetchAgenda(); }}
      />

      {/* Edit Booking Dialog */}
      {showEditBooking && (
        <EditBookingDialog
          open={!!showEditBooking}
          onClose={() => setShowEditBooking(null)}
          booking={showEditBooking}
          token={token}
          boats={boats}
          onSuccess={() => { setShowEditBooking(null); fetchAgenda(); }}
        />
      )}

      {/* Block Dates Dialog */}
      {showBlockDates && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setShowBlockDates(false)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6 m-4" onClick={e => e.stopPropagation()}>
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                <Ban className="w-5 h-5 text-red-500" />
              </div>
              <div>
                <h3 className="text-lg font-heading text-[#0a57a4]">Bloquer des dates</h3>
                <p className="text-xs text-[#858275]">Les clients ne pourront pas réserver ces dates</p>
              </div>
            </div>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs text-[#0a57a4]">Date début</Label>
                  <Input type="date" value={blockForm.start_date} onChange={e => setBlockForm(p => ({ ...p, start_date: e.target.value, end_date: p.end_date || e.target.value }))} data-testid="block-start-date" />
                </div>
                <div>
                  <Label className="text-xs text-[#0a57a4]">Date fin</Label>
                  <Input type="date" value={blockForm.end_date} onChange={e => setBlockForm(p => ({ ...p, end_date: e.target.value }))} min={blockForm.start_date} data-testid="block-end-date" />
                </div>
              </div>
              <div>
                <Label className="text-xs text-[#0a57a4]">Motif (optionnel)</Label>
                <Input value={blockForm.reason} onChange={e => setBlockForm(p => ({ ...p, reason: e.target.value }))} placeholder="Ex: BMS - Bulletin Météo Spécial, Maintenance..." data-testid="block-reason" />
              </div>
              <div className="flex gap-3 pt-2">
                <Button variant="outline" className="flex-1" onClick={() => setShowBlockDates(false)}>Annuler</Button>
                <Button className="flex-1 bg-red-500 hover:bg-red-600 text-white" onClick={createBlockedDates} data-testid="block-confirm-btn">
                  <Ban className="w-4 h-4 mr-2" /> Bloquer
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function BookingChip({ booking, boatIndex, compact, onClick }) {
  const sc = STATUS_COLORS[booking.status] || STATUS_COLORS.pending;
  return (
    <div
      onClick={onClick}
      className="rounded-sm px-1.5 py-0.5 text-[10px] cursor-pointer hover:opacity-80 transition-opacity border-l-[3px] truncate"
      style={{ backgroundColor: sc.bg, color: sc.text, borderLeftColor: BOAT_COLORS[boatIndex] || '#666' }}
      title={`${booking.customer?.first_name} ${booking.customer?.last_name} — ${booking.time_slot}`}
      data-testid={`booking-chip-${booking.id?.slice(0, 8)}`}
    >
      {compact ? (
        <span>{booking.time_slot?.split('-')[0]}</span>
      ) : (
        <span>{booking.time_slot?.split('-')[0]} {booking.customer?.first_name?.charAt(0)}.{booking.customer?.last_name}</span>
      )}
    </div>
  );
}

function DayView({ day, boats, bookings, onEdit, onNewSlot }) {
  const hours = Array.from({ length: 12 }, (_, i) => i + 8);
  
  const getBookingPosition = (booking) => {
    const slot = booking.time_slot || '';
    const parts = slot.split('-');
    if (parts.length < 2) return null;
    const startParts = parts[0].replace('h', ':').split(':');
    const startH = parseInt(startParts[0]);
    const startM = parseInt(startParts[1] || '0');
    const top = ((startH - 8) + startM / 60) * 60;
    const height = (booking.duration_hours || 2) * 60;
    return { top: Math.max(0, top), height: Math.max(30, height) };
  };

  return (
    <div className="bg-white rounded-sm border border-[#dec5ae]/30 overflow-x-auto">
      <div className="flex min-w-[320px] lg:min-w-[700px]">
        {/* Time column */}
        <div className="w-10 lg:w-16 flex-shrink-0 bg-[#FDFBF7] border-r border-[#dec5ae]/20">
          <div className="h-10 border-b border-[#dec5ae]/20" />
          {hours.map(h => (
            <div key={h} className="h-[60px] border-b border-[#dec5ae]/10 flex items-start justify-end pr-2 pt-1">
              <span className="text-[10px] text-[#858275]">{h}h</span>
            </div>
          ))}
        </div>
        
        {/* Boat columns */}
        {boats.map((boat, bi) => {
          const boatBookings = bookings.filter(b => b.boat_id === boat.id);
          return (
            <div key={boat.id} className="flex-1 border-r border-[#dec5ae]/20 last:border-r-0 relative">
              <div className="h-10 flex items-center justify-center border-b border-[#dec5ae]/20 bg-[#FDFBF7]">
                <div className="flex items-center gap-1.5">
                  <div className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: BOAT_COLORS[bi] }} />
                  <span className="text-xs font-medium text-[#0a57a4]">{BOAT_LABELS[bi]}</span>
                </div>
              </div>
              <div className="relative">
                {hours.map(h => (
                  <div
                    key={h}
                    className="h-[60px] border-b border-[#dec5ae]/10 cursor-pointer hover:bg-[#099dbb]/5 transition-colors"
                    onClick={() => onNewSlot({ date: format(day, 'yyyy-MM-dd'), boat_id: boat.id, hour: h })}
                  />
                ))}
                {/* Booking blocks */}
                {boatBookings.map(b => {
                  const pos = getBookingPosition(b);
                  if (!pos) return null;
                  const sc = STATUS_COLORS[b.status] || STATUS_COLORS.pending;
                  return (
                    <motion.div
                      key={b.id}
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      className="absolute left-1 right-1 rounded-sm px-2 py-1 cursor-pointer hover:shadow-md transition-shadow border overflow-hidden"
                      style={{ top: pos.top, height: pos.height, backgroundColor: sc.bg, borderColor: sc.border }}
                      onClick={() => onEdit(b)}
                      data-testid={`day-booking-${b.id?.slice(0, 8)}`}
                    >
                      <p className="text-[10px] font-bold truncate" style={{ color: sc.text }}>{b.time_slot}</p>
                      <p className="text-[10px] truncate" style={{ color: sc.text }}>{b.customer?.first_name} {b.customer?.last_name}</p>
                      {pos.height > 50 && <p className="text-[10px] truncate" style={{ color: sc.text }}>{b.passengers} pers. — {(b.total_price / 100).toFixed(0)}€</p>}
                      {b.payment_status === 'on_site' && <p className="text-[9px] font-semibold mt-0.5" style={{ color: sc.text }}>Paiement sur place</p>}
                    </motion.div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function NewBookingDialog({ open, onClose, token, boats, prefill, onSuccess }) {
  const [form, setForm] = useState({
    date: prefill?.date || format(new Date(), 'yyyy-MM-dd'),
    time_start: prefill?.hour ? `${prefill.hour}h00` : '10h00',
    duration_hours: 3,
    passengers: 2,
    boat_id: prefill?.boat_id || 'auto',
    customer_first_name: '',
    customer_last_name: '',
    customer_email: '',
    customer_phone: '',
    total_price: 16000,
    notes: 'Paiement sur place',
    send_confirmation_email: false,
    create_payment_link: false
  });
  const [saving, setSaving] = useState(false);
  const [paymentLinkResult, setPaymentLinkResult] = useState(null);

  useEffect(() => {
    if (prefill) {
      setForm(prev => ({
        ...prev,
        date: prefill.date || prev.date,
        boat_id: prefill.boat_id || prev.boat_id,
        time_start: prefill.hour ? `${prefill.hour}h00` : prev.time_start
      }));
    }
  }, [prefill]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.customer_first_name || !form.customer_last_name || !form.customer_email || !form.customer_phone) {
      toast.error('Veuillez remplir tous les champs client');
      return;
    }
    setSaving(true);
    try {
      const startParts = form.time_start.split('h');
      const startH = parseInt(startParts[0]);
      const startM = parseInt(startParts[1] || '0');
      const totalMinutes = (startH * 60 + startM) + (form.duration_hours * 60);
      const endH = Math.floor(totalMinutes / 60);
      const endM = totalMinutes % 60;
      const timeSlot = `${form.time_start}-${endH}h${endM.toString().padStart(2, '0')}`;
      const res = await fetch(`${API}/admin/bookings/manual`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          boat_id: form.boat_id === 'auto' ? '' : form.boat_id,
          time_slot: timeSlot
        })
      });
      if (!res.ok) {
        const text = await res.text();
        let msg = 'Erreur';
        try { msg = JSON.parse(text).detail || msg; } catch {}
        throw new Error(msg);
      }
      const data = await res.json();
      if (data.payment_link) {
        setPaymentLinkResult(data.payment_link);
        toast.success('Réservation créée + lien de paiement envoyé !');
      } else {
        toast.success('Réservation créée avec succès');
        onSuccess();
      }
    } catch (e) {
      toast.error(e.message || 'Erreur lors de la création');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-[#0a57a4] flex items-center gap-2">
            <Plus className="w-5 h-5" /> Nouvelle réservation manuelle
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-[#0a57a4]">Date</Label>
              <Input type="date" value={form.date} onChange={e => setForm(p => ({ ...p, date: e.target.value }))} className="text-sm" data-testid="new-booking-date" />
            </div>
            <div>
              <Label className="text-xs text-[#0a57a4]">Heure de début</Label>
              <Select value={form.time_start} onValueChange={v => setForm(p => ({ ...p, time_start: v }))}>
                <SelectTrigger className="text-sm" data-testid="new-booking-time"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Array.from({ length: 12 }, (_, i) => i + 8).flatMap(h => [
                    <SelectItem key={`${h}h00`} value={`${h}h00`}>{h}h00</SelectItem>,
                    <SelectItem key={`${h}h30`} value={`${h}h30`}>{h}h30</SelectItem>
                  ])}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label className="text-xs text-[#0a57a4]">Durée (h)</Label>
              <Select value={String(form.duration_hours)} onValueChange={v => setForm(p => ({ ...p, duration_hours: Number(v) }))}>
                <SelectTrigger className="text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {[2, 3, 4, 5, 6].map(d => (<SelectItem key={d} value={String(d)}>{d}h</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-[#0a57a4]">Passagers</Label>
              <Select value={String(form.passengers)} onValueChange={v => setForm(p => ({ ...p, passengers: Number(v) }))}>
                <SelectTrigger className="text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {[1, 2, 3, 4, 5].map(p => (<SelectItem key={p} value={String(p)}>{p}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-[#0a57a4]">Bateau</Label>
              <Select value={form.boat_id} onValueChange={v => setForm(p => ({ ...p, boat_id: v }))}>
                <SelectTrigger className="text-sm"><SelectValue placeholder="Auto" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="auto">Auto (premier dispo)</SelectItem>
                  {boats.map((b, i) => (<SelectItem key={b.id} value={b.id}>{BOAT_LABELS[i]}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="border-t border-[#dec5ae]/30 pt-3">
            <h4 className="text-xs font-semibold text-[#0a57a4] mb-2">Client</h4>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-[#858275]">Prénom *</Label>
                <Input value={form.customer_first_name} onChange={e => setForm(p => ({ ...p, customer_first_name: e.target.value }))} className="text-sm" data-testid="new-booking-firstname" />
              </div>
              <div>
                <Label className="text-xs text-[#858275]">Nom *</Label>
                <Input value={form.customer_last_name} onChange={e => setForm(p => ({ ...p, customer_last_name: e.target.value }))} className="text-sm" data-testid="new-booking-lastname" />
              </div>
              <div>
                <Label className="text-xs text-[#858275]">Email *</Label>
                <Input type="email" value={form.customer_email} onChange={e => setForm(p => ({ ...p, customer_email: e.target.value }))} className="text-sm" data-testid="new-booking-email" />
              </div>
              <div>
                <Label className="text-xs text-[#858275]">Téléphone *</Label>
                <Input value={form.customer_phone} onChange={e => setForm(p => ({ ...p, customer_phone: e.target.value }))} className="text-sm" data-testid="new-booking-phone" />
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-[#0a57a4]">Montant TTC (€)</Label>
              <Input type="number" value={form.total_price / 100} onChange={e => setForm(p => ({ ...p, total_price: Math.round(Number(e.target.value) * 100) }))} className="text-sm" data-testid="new-booking-price" />
              <p className="text-[10px] text-[#858275] mt-1">HT: {(form.total_price / 100 / 1.20).toFixed(2)}€ | TVA 20%: {(form.total_price / 100 - form.total_price / 100 / 1.20).toFixed(2)}€</p>
            </div>
            <div>
              <Label className="text-xs text-[#0a57a4]">Notes internes (admin)</Label>
              <Input value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} className="text-sm" placeholder="Ex: Paiement sur place, client fidèle..." data-testid="new-booking-notes" />
            </div>
          </div>
          {/* Options */}
          <div className="border-t border-[#dec5ae]/30 pt-3 space-y-2">
            <label className="flex items-center gap-2 cursor-pointer" data-testid="new-booking-send-email">
              <input 
                type="checkbox" 
                checked={form.send_confirmation_email} 
                onChange={e => setForm(p => ({ ...p, send_confirmation_email: e.target.checked }))} 
                className="w-4 h-4 rounded border-[#dec5ae] text-[#0a57a4] focus:ring-[#0a57a4]"
              />
              <span className="text-sm text-[#0a57a4]">Envoyer confirmation par email au client</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer" data-testid="new-booking-payment-link">
              <input 
                type="checkbox" 
                checked={form.create_payment_link} 
                onChange={e => setForm(p => ({ ...p, create_payment_link: e.target.checked }))} 
                className="w-4 h-4 rounded border-[#dec5ae] text-[#099dbb] focus:ring-[#099dbb]"
              />
              <span className="text-sm text-[#099dbb]">Créer un lien de paiement Square et l'envoyer</span>
            </label>
          </div>
          {/* Payment link result */}
          {paymentLinkResult && (
            <div className="bg-green-50 border border-green-200 rounded p-3">
              <p className="text-sm font-medium text-green-800 mb-1">Lien de paiement créé :</p>
              <a href={paymentLinkResult} target="_blank" rel="noopener noreferrer" className="text-xs text-[#099dbb] underline break-all">{paymentLinkResult}</a>
              <div className="flex gap-2 mt-2">
                <Button type="button" size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(paymentLinkResult); toast.success('Lien copié !'); }}>
                  Copier le lien
                </Button>
                <Button type="button" size="sm" variant="outline" onClick={() => { setPaymentLinkResult(null); onSuccess(); }}>
                  Fermer
                </Button>
              </div>
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose}>Annuler</Button>
            <Button type="submit" disabled={saving} className="bg-[#099dbb] hover:bg-[#0a57a4] text-white" data-testid="new-booking-submit">
              {saving ? 'Enregistrement...' : 'Créer la réservation'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function EditBookingDialog({ open, onClose, booking, token, boats, onSuccess }) {
  const [form, setForm] = useState({
    date: booking.date,
    time_slot: booking.time_slot,
    duration_hours: booking.duration_hours,
    boat_id: booking.boat_id,
    status: booking.status,
    notes: booking.notes || ''
  });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [sendingReport, setSendingReport] = useState(false);

  const sc = STATUS_COLORS[booking.status] || STATUS_COLORS.pending;
  const ttc = (booking.total_price / 100).toFixed(2);
  const ht = (booking.total_price / 100 / 1.20).toFixed(2);
  const tva = (ttc - ht).toFixed(2);

  const handleReschedule = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/admin/bookings/${booking.id}/reschedule`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });
      if (!res.ok) throw new Error('Erreur');
      toast.success('Réservation modifiée');
      onSuccess();
    } catch {
      toast.error('Erreur lors de la modification');
    } finally {
      setSaving(false);
    }
  };

  const handleStatusUpdate = async (status) => {
    try {
      const res = await fetch(`${API}/admin/bookings/${booking.id}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ status })
      });
      if (!res.ok) throw new Error('Erreur');
      toast.success('Statut mis à jour');
      onSuccess();
    } catch {
      toast.error('Erreur');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Supprimer cette réservation ?')) return;
    setDeleting(true);
    try {
      const res = await fetch(`${API}/admin/bookings/${booking.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Erreur');
      toast.success('Réservation supprimée');
      onSuccess();
    } catch {
      toast.error('Erreur');
    } finally {
      setDeleting(false);
    }
  };

  const handleSendConditionReport = async () => {
    if (!window.confirm("Envoyer l'état des lieux + CGV par email au client ?")) return;
    setSendingReport(true);
    try {
      const res = await fetch(`${API}/admin/bookings/${booking.id}/send-condition-report`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.detail || 'Erreur'); }
      const data = await res.json();
      toast.success(data.message || "État des lieux envoyé");
    } catch (e) {
      toast.error(e.message || "Erreur d'envoi");
    } finally {
      setSendingReport(false);
    }
  };

  const handlePreviewPdf = () => {
    window.open(`${API}/admin/bookings/${booking.id}/condition-report-pdf?token=${token}`, '_blank');
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-[#0a57a4] flex items-center gap-2">
            <Pencil className="w-5 h-5" /> Détails de la réservation
          </DialogTitle>
        </DialogHeader>

        {/* Client info */}
        <div className="bg-[#FDFBF7] p-3 rounded-sm border border-[#dec5ae]/30 space-y-1">
          <div className="flex items-center justify-between">
            <p className="text-sm font-semibold text-[#0a57a4]">{booking.customer?.first_name} {booking.customer?.last_name}</p>
            <span className="text-xs px-2 py-0.5 rounded-sm" style={{ backgroundColor: sc.bg, color: sc.text }}>{STATUS_LABELS[booking.status]}</span>
          </div>
          <p className="text-xs text-[#858275]">{booking.customer?.email} | {booking.customer?.phone}</p>
          <div className="flex items-center gap-4 text-xs text-[#858275] pt-1 border-t border-[#dec5ae]/20 mt-1">
            <span>TTC: <strong className="text-[#0a57a4]">{ttc}€</strong></span>
            <span>HT: {ht}€</span>
            <span>TVA 20%: {tva}€</span>
            {booking.payment_status === 'on_site' && <span className="text-orange-600 font-semibold">Paiement sur place</span>}
          </div>
        </div>

        {/* Reschedule form */}
        <div className="space-y-3">
          <h4 className="text-xs font-semibold text-[#0a57a4] flex items-center gap-1"><CloudRain className="w-3.5 h-3.5" /> Modifier / Déplacer</h4>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-[#858275]">Date</Label>
              <Input type="date" value={form.date} onChange={e => setForm(p => ({ ...p, date: e.target.value }))} className="text-sm" data-testid="edit-booking-date" />
            </div>
            <div>
              <Label className="text-xs text-[#858275]">Créneau</Label>
              <Input value={form.time_slot} onChange={e => setForm(p => ({ ...p, time_slot: e.target.value }))} className="text-sm" data-testid="edit-booking-slot" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-[#858275]">Durée (h)</Label>
              <Select value={String(form.duration_hours)} onValueChange={v => setForm(p => ({ ...p, duration_hours: Number(v) }))}>
                <SelectTrigger className="text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {[2, 3, 4, 5, 6].map(d => (<SelectItem key={d} value={String(d)}>{d}h</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-[#858275]">Bateau</Label>
              <Select value={form.boat_id} onValueChange={v => setForm(p => ({ ...p, boat_id: v }))}>
                <SelectTrigger className="text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {boats.map((b, i) => (<SelectItem key={b.id} value={b.id}>{BOAT_LABELS[i]}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label className="text-xs text-[#858275]">Notes internes (admin)</Label>
            <Textarea value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} rows={2} className="text-sm resize-none" placeholder="Ex: Déplacé pour mauvais temps, client fidèle..." />
          </div>
          <Button onClick={handleReschedule} disabled={saving} className="w-full bg-[#0a57a4] hover:bg-[#099dbb] text-white text-sm" data-testid="edit-booking-save">
            {saving ? 'Enregistrement...' : 'Enregistrer les modifications'}
          </Button>
        </div>

        {/* Status & actions */}
        <div className="border-t border-[#dec5ae]/30 pt-3 space-y-3">
          <h4 className="text-xs font-semibold text-[#858275]">Changer le statut</h4>
          <div className="flex flex-wrap gap-2">
            {Object.entries(STATUS_LABELS).map(([key, label]) => (
              <button
                key={key}
                onClick={() => handleStatusUpdate(key)}
                className={`text-xs px-3 py-1.5 rounded-sm border transition-colors ${booking.status === key ? 'ring-2 ring-offset-1' : 'hover:opacity-80'}`}
                style={{ backgroundColor: STATUS_COLORS[key].bg, color: STATUS_COLORS[key].text, borderColor: STATUS_COLORS[key].border }}
                data-testid={`status-${key}`}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Condition Report Actions */}
          <div className="border-t border-[#dec5ae]/30 pt-3 space-y-2">
            <h4 className="text-xs font-semibold text-[#858275] flex items-center gap-1">
              <ClipboardCheck className="w-3.5 h-3.5" /> État des lieux
            </h4>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleSendConditionReport}
                disabled={sendingReport}
                className="flex-1 text-xs border-[#0a57a4]/30 text-[#0a57a4] hover:bg-[#0a57a4]/5"
                data-testid="send-condition-report-btn"
              >
                <Send className="w-3.5 h-3.5 mr-1" />
                {sendingReport ? 'Envoi...' : 'Envoyer état des lieux + CGV'}
              </Button>
            </div>
            {booking.condition_report_sent && (
              <p className="text-xs text-green-600 flex items-center gap-1">
                <ClipboardCheck className="w-3 h-3" /> Envoyé le {booking.condition_report_sent_at ? new Date(booking.condition_report_sent_at).toLocaleDateString('fr-FR') : ''}
                {booking.condition_report_signed ? (
                  <span className="ml-2 text-green-700 font-semibold">— Signé par le client</span>
                ) : (
                  <span className="ml-2 text-orange-500">— En attente de signature</span>
                )}
              </p>
            )}
          </div>

          <Button variant="outline" onClick={handleDelete} disabled={deleting} className="w-full text-red-600 border-red-200 hover:bg-red-50 text-sm" data-testid="delete-booking-btn">
            {deleting ? 'Suppression...' : 'Supprimer la réservation'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
